/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {error} from '@ion/core/src/logger/logger';
import {
  enableFactor as enableFactorService,
  enrollFactor as enrollFactorService,
  resetMfa as resetMfaService,
  submitTotp as submitTotpService
} from '@ion/core/src/services/Connect';
import {readRotatedIntermediateToken} from '@ion/core/src/services/ConnectUtils';
import {User} from '@ion/core/src/types/entities';
import {
  LoginResult,
  MfaEnrollmentFactor,
  MfaProvider,
  MfaRateLimitError,
  MfaReusedCodeError,
  MfaTransactionTerminalError,
  OktaFactor,
  TOTP_FACTOR_TYPE,
  TotpFactorType
} from '@ion/core/src/types/mfa';

/**
 * The lifetime of a single-use MFA intermediate token in milliseconds (300
 * seconds per the platform contract). An MFA transaction older than this is
 * treated as expired and returns the user to the login screen.
 */
export const MFA_TRANSACTION_TTL_MS = 300000;

/**
 * Thrown when an in-flight MFA request resolves against a transaction that has
 * since been cancelled or replaced (for example the user backed out to the login
 * screen while `/mfa/totp` was pending). Distinct from a fatal/expired-session
 * error so the caller can silently abort without marking the session expired.
 */
export class MfaTransactionCancelledError extends Error {
  public constructor(message = 'The MFA transaction was cancelled.') {
    super(message);
    this.name = 'MfaTransactionCancelledError';
    Object.setPrototypeOf(this, MfaTransactionCancelledError.prototype);
  }
};

/**
 * Matches the backend error text for a rejected TOTP passcode across the verify
 * ("Authentication failed. Invalid Passcode/Answer") and enrollment
 * ("invalid pass code") endpoints. Used to distinguish a wrong-code `403` from a
 * genuine permissions/expired-token `403`, which must keep the existing
 * session-expired handling.
 */
const INVALID_PASSCODE_PATTERN = /invalid.*(pass\s*code|answer)/i;

/**
 * Matches the backend error text for a resubmitted TOTP passcode that was
 * already consumed ("Each code can only be used once"). Kept as its own
 * pattern, distinct from `INVALID_PASSCODE_PATTERN`, so a reused code is
 * classified separately and the passcode UI can show its own "wait for a new
 * code" message rather than the generic invalid-code message.
 */
const REUSED_PASSCODE_PATTERN = /code can only be used once/i;

/**
 * The transient, in-memory context for an in-progress Multi-Factor Authentication
 * (MFA) transaction. It is never persisted to web storage; a page refresh discards
 * it and returns the user to the login screen.
 */
export interface MfaContext {
  phase: 'enroll' | 'challenge' | 'activation';
  intermediateToken: string;
  transactionExpiresAt?: number;
  username?: string;
  stateToken?: string;
  factorId?: string;
  oktaUserId?: string;
  factorType?: TotpFactorType;
  provider?: MfaProvider;
  expiresAt?: string;
  factors?: MfaEnrollmentFactor[];
  qrCodeUrl?: string;
  sharedSecret?: string;
  selectedProvider?: MfaProvider;
  emailHint?: string;
};

/**
 * The public MFA surface implemented by `AuthenticationManager`, whose
 * delegating methods forward to `MFA` where the two APIs genuinely share a
 * signature. `MFA`'s own internal API is free to diverge (see
 * `MfaSubmitTotpResult`) where it needs to hand richer data back to its host.
 */
export interface MFAClient {
  enrollFactor(provider: MfaProvider): Promise<OktaFactor>;
  beginActivation(): void;
  enableFactor(passCode: string): Promise<void>;
  submitTotp(passCode: string): Promise<User>;
  resetMfa(): Promise<string | undefined>;
  getMfaContext(): MfaContext | null;
  isMfaSessionExpired(): boolean;
  cancelMfa(): void;
  clearMfaContext(): void;
}

/**
 * The successful-verification data `MFA.submitTotp()` hands back to its
 * caller so the outer CTC session can be completed. `MFA` never completes the
 * session itself - it has no knowledge of `AuthenticationManager` or its OIDC
 * auth state.
 */
export interface MfaSubmitTotpResult {
  authToken: string;
  username: string;
}

/**
 * Owns the in-memory MFA transaction context and every MFA-specific request
 * (enrollment, activation, TOTP verification, reset). `AuthenticationManager`
 * holds a single instance, delegates its public MFA methods to it, and
 * translates its results into OIDC auth state transitions - `MFA` itself
 * never calls back into `AuthenticationManager`.
 */
export class MFA {
  private _context: MfaContext | null = null;
  private _sessionExpired = false;

  /**
   * Clears the session-expired flag at the start of a new login attempt.
   */
  public startLoginAttempt(): void {
    this._sessionExpired = false;
  }

  /**
   * Branches a non-`authenticated` `authenticateMfa()` result into the enroll
   * or challenge MFA phase, returning which phase was entered so the caller
   * can translate it into its own auth state. A 202 that arrives without a
   * readable `X-Intermediate-Token` (for example when the header is not
   * CORS-exposed) cannot continue as an MFA transaction and throws here
   * rather than 401 on the next request.
   */
  public beginTransaction(
    result: Extract<LoginResult, {status: 'enroll' | 'challenge'}>,
    username: string
  ): 'enroll' | 'challenge' {
    if (!result.intermediateToken) {
      const missingToken = new Error(
        'The MFA login response did not include an intermediate token.'
      );
      this.handleMfaError(missingToken);
      throw missingToken;
    } else if (result.status === 'enroll') {
      this._context = {
        phase                : 'enroll',
        username,
        intermediateToken    : result.intermediateToken,
        transactionExpiresAt : Date.now() + MFA_TRANSACTION_TTL_MS,
        factors              : result.factors
      };
      return 'enroll';
    } else {
      this._context = {
        phase                : 'challenge',
        username,
        intermediateToken    : result.intermediateToken,
        transactionExpiresAt : Date.now() + MFA_TRANSACTION_TTL_MS,
        stateToken           : result.stateToken,
        factorId             : result.factorId,
        oktaUserId           : result.oktaUserId,
        expiresAt            : result.expiresAt
      };
      return 'challenge';
    }
  }

  /**
   * Retrieves the QR setup data for a selected TOTP provider by enrolling the
   * factor, storing the returned `factorId` and rotated intermediate token in
   * the MFA context.
   */
  public async enrollFactor(provider: MfaProvider): Promise<OktaFactor> {
    this.assertMfaTransactionActive();
    const ctx      = this._context!;
    const selected = ctx.factors?.find((factor) => factor.provider === provider) || ctx.factors?.[0];
    try {
      const resp   = await enrollFactorService(
        selected!.oktaUserId,
        TOTP_FACTOR_TYPE,
        provider,
        ctx.intermediateToken
      );
      if (!resp.intermediateToken) {
        // enrollFactor is contractually required to rotate the intermediate
        // token; continuing with the consumed one guarantees a 401 later ...
        throw new Error('The enrollFactor response did not rotate the intermediate token.');
      }
      const factor = resp.oktaFactor;
      this._context = {
        ...ctx,
        intermediateToken    : resp.intermediateToken,
        transactionExpiresAt : Date.now() + MFA_TRANSACTION_TTL_MS,
        oktaUserId           : factor?.oktaUserId || selected?.oktaUserId,
        factorId             : factor?.factorId,
        factorType           : TOTP_FACTOR_TYPE,
        provider,
        selectedProvider     : provider,
        qrCodeUrl            : factor?.qrCodeUrl,
        sharedSecret         : factor?.sharedSecret
      };
      return factor;
    } catch (e) {
      this.handleMfaError(e);
      throw e;
    }
  }

  /**
   * Transitions the enrollment UI from QR display into TOTP activation. The
   * MFA phase remains `activation`.
   */
  public beginActivation(): void {
    if (this._context) {
      this._context = {...this._context, phase: 'activation'};
    }
  }

  /**
   * Activates the pending TOTP factor with the first passcode. Per the
   * platform contract activation never returns an auth token, so on success
   * it clears the MFA context and resolves; the user re-authenticates to
   * obtain a session.
   */
  public async enableFactor(passCode: string): Promise<void> {
    this.assertMfaTransactionActive();
    const ctx = this._context!;
    try {
      await enableFactorService(ctx.oktaUserId!, ctx.factorId!, passCode, ctx.intermediateToken);
      // Per the platform contract `/mfa/enableFactor` never returns an auth token;
      // activation is terminal and the user re-authenticates. Clear the context so
      // the consumed transaction cannot be reused ...
      this.clearMfaContext();
    } catch (e) {
      this.handleMfaSubmissionError(e, ctx);
    }
  }

  /**
   * Verifies a returning user's TOTP passcode and, on success, returns the
   * auth token and login context the caller needs to complete its own CTC
   * session. `MFA` never establishes the session itself.
   */
  public async submitTotp(passCode: string): Promise<MfaSubmitTotpResult> {
    this.assertMfaTransactionActive();
    const context = this._context!;
    let resp;
    try {
      resp = await submitTotpService(context.stateToken!, context.factorId!, passCode, context.intermediateToken);
    } catch (e) {
      this.handleMfaSubmissionError(e, context);
    }
    if (this._context !== context) {
      // the transaction was cancelled or replaced while the request was in
      // flight; abort without completing login or touching session state ...
      throw new MfaTransactionCancelledError();
    }
    if (!resp.authToken) {
      const invalid = new Error('The MFA verification response did not include an auth token.');
      this.handleMfaError(invalid);
      throw invalid;
    }
    const result = {authToken: resp.authToken, username: context.username!};
    this.clearMfaContext();
    return result;
  }

  /**
   * Starts the email-based MFA reset ("set up a new device"), clears the MFA
   * context, and returns the masked email hint when provided.
   */
  public async resetMfa(): Promise<string | undefined> {
    this.assertMfaTransactionActive();
    const ctx       = this._context!;
    const resp      = await resetMfaService(ctx.oktaUserId!, ctx.factorId!, ctx.intermediateToken);
    const emailHint = resp.emailHint;
    this.clearMfaContext();
    return emailHint;
  }

  public getMfaContext(): MfaContext | null {
    return this._context;
  }

  public isMfaSessionExpired(): boolean {
    return this._sessionExpired;
  }

  public cancelMfa(): void {
    this.clearMfaContext();
  }

  public clearMfaContext(): void {
    this._context = null;
  }

  private isInvalidCodeError(e: any): boolean {
    return Boolean(e?.is403 || e?.isPermissionsError) &&
      INVALID_PASSCODE_PATTERN.test(e?.errorText || '');
  }

  private isReusedCodeError(e: any): boolean {
    return Boolean(e?.is403 || e?.isPermissionsError) &&
      REUSED_PASSCODE_PATTERN.test(e?.errorText || '');
  }

  private isRateLimited(e: any): boolean {
    return Boolean(e?.is429 || e?.isRateLimitError || e?.status === 429);
  }

  /**
   * Routes a failed `/mfa/enableFactor` or `/mfa/totp` request. A message-gated
   * `403` — an invalid passcode, or a previously-consumed passcode ("Each code
   * can only be used once") — that carries a rotated intermediate token rotates
   * the captured transaction and re-throws for inline retry (the reused-code
   * case as an `MfaReusedCodeError` so the passcode UI can show its own
   * message); a `403` with no replacement token, or any `401` (a
   * consumed/expired intermediate token), ends the transaction terminally with
   * `MfaTransactionTerminalError` while leaving the user on the passcode
   * screen. A `429` means the identity provider has temporarily restricted the
   * user and is handled before either, since the restriction is counted
   * upstream and a further submission cannot succeed. Every other error is
   * fatal and returns the UI to login.
   */
  private handleMfaSubmissionError(e: any, context: MfaContext): never {
    if (this.isRateLimited(e)) {
      this.clearMfaContext();
      throw new MfaRateLimitError();
    }
    const reused  = this.isReusedCodeError(e);
    const invalid = this.isInvalidCodeError(e);
    if (reused || invalid) {
      const rotated = readRotatedIntermediateToken(e);
      if (rotated) {
        this.rotateMfaContext(context, rotated);
        if (reused) {
          throw new MfaReusedCodeError();
        }
        throw e;
      }
      this.endMfaTransaction();
    }
    if (e?.is401 || e?.isUnauthorizedError) {
      this.endMfaTransaction();
    }
    this.handleMfaError(e);
    throw e;
  }

  /**
   * Replaces the active transaction's single-use intermediate token with the
   * rotated value returned on an invalid-passcode `403` and refreshes the
   * transaction lifetime. Throws `MfaTransactionCancelledError` when the captured
   * `context` is no longer the active transaction (the user cancelled or started a
   * new transaction while the request was in flight), so a late response never
   * overwrites the replacement transaction with a stale token.
   */
  private rotateMfaContext(context: MfaContext, intermediateToken: string): void {
    if (this._context !== context) {
      throw new MfaTransactionCancelledError();
    }
    this._context = {
      ...context,
      intermediateToken,
      transactionExpiresAt : Date.now() + MFA_TRANSACTION_TTL_MS
    };
  }

  /**
   * Ends the MFA transaction terminally without leaving the passcode screen: the
   * context is cleared so the consumed token can never be resubmitted, but the auth
   * state is left untouched (so `VerifyCode` stays mounted) and the session is not
   * marked expired (so the login-screen banner does not fire). The passcode UI
   * renders its terminal "sign in again to restart" panel from the thrown error.
   */
  private endMfaTransaction(): never {
    this.clearMfaContext();
    throw new MfaTransactionTerminalError();
  }

  /**
   * Throws when the in-flight MFA transaction has outlived the 300 second
   * intermediate-token lifetime, clearing the context and returning the UI
   * to the login screen with the session-expired message.
   */
  private assertMfaTransactionActive(): void {
    const expiresAt = this._context?.transactionExpiresAt;
    if (expiresAt && Date.now() > expiresAt) {
      const expired = new Error('The MFA transaction has expired.');
      this.handleMfaError(expired);
      throw expired;
    }
  }

  /**
   * Logs the failure, marks the MFA session expired, and clears the
   * transient MFA context. Used for fatal/expired-token errors, never for a
   * wrong passcode (which is retried inline) or a cancelled transaction
   * (which aborts silently). The caller is expected to inspect
   * `isMfaSessionExpired()` after catching the propagated error and return
   * its own outer auth state to login when set.
   */
  private handleMfaError(e: any): void {
    error(`An MFA request failed; clearing MFA context. ${e?.toString?.() || ''}`);
    this._sessionExpired = true;
    this.clearMfaContext();
  }
};
